import type { PlatformType, PublishablePlatformType } from '../db/schema/platform'
import type { WorkspaceMedia } from '../db/typeRoots/workspaceMedia'
import { postContent as linkedinPostContent } from './linkedin/lib/postContent'
import { postContent as instagramPostContent } from './instagram/lib/postContent'
import { postContent as tiktokPostContent } from './tiktok/lib/postContent'
import { postContent as youtubePostContent } from './youtube/lib/postContent'
import { postContent as facebookPostContent } from './facebook/lib/postContent'
import type { PostContent as LinkedInPostContent } from './linkedin/api/createPost'
import type { PostContent as InstagramPostContent } from './instagram/api/createPost'
import type { PostContent as TikTokPostContent } from './tiktok/api/createPost'
import type { PostContent as YouTubePostContent } from './youtube/api/createPost'
import type { PostContent as FacebookPostContent } from './facebook/api/createPost'

// Subset of the workspace this builder needs. Frontend callers build it from
// local component state; backend callers from a fetched workspace row.
export type WorkspaceLike = {
  title: string
  captions: Partial<Record<PlatformType, string>>
  titles: Partial<Record<PlatformType, string>>
  media: WorkspaceMedia[]
}

// Per-call flags that aren't (yet) stored on the workspace. The preview
// modal passes these from UI toggles; the chat agent and API/MCP default
// to plain feed posts.
export type WorkspacePostFlags = {
  /** Instagram + Facebook: post as story instead of feed. */
  isStory?: boolean
  /** Facebook: post a single video as a reel. */
  isReel?: boolean
}

// Discriminated union shaped so spread `...result.content` into the
// `platform.post` / `createScheduledPost` inputs is type-safe and
// cast-free.
export type WorkspacePostContent =
  | { platform: 'linkedin'; postContent: LinkedInPostContent }
  | { platform: 'instagram'; postContent: InstagramPostContent }
  | { platform: 'tiktok'; postContent: TikTokPostContent }
  | { platform: 'youtube'; postContent: YouTubePostContent }
  | { platform: 'facebook'; postContent: FacebookPostContent }

export type BuildPostContentResult =
  | { ok: true; content: WorkspacePostContent }
  | { ok: false; reason: string }

// Hardcoded MIME mapping matches what the preview modal and the workspace
// chat do today. Canvas previews are always PNG (set by autodesign); image
// allowlists in every platform accept image/png and video/mp4 so the
// platform helpers' file-type filtering still gates correctly. Switching to
// `m.file.file.type` (the actual MIME the user uploaded) is a separate
// concern — kept in lockstep for now so this unification doesn't smuggle in
// a behavior change.
const fileTypeFromMediaType = (mediaType: WorkspaceMedia['type']): string => {
  if (mediaType === 'canvas' || mediaType === 'image') return 'image/png'
  if (mediaType === 'video') return 'video/mp4'
  return 'application/pdf'
}

// The one place workspace → postContent conversion lives. Called by:
//   - frontend/.../workspace-preview-modal.tsx (publish + schedule, all 5 platforms)
//   - frontend/.../workspace-page.tsx (chat agent's onSchedulePost)
//   - services/api/.../workspacePost.ts (REST + MCP, via resolvePlatformInput)
//
// Imports are intentionally direct paths into each platform's
// `lib/postContent` (and `import type` from `api/createPost`) so the
// frontend bundle stays clear of the heavyweight backend modules
// (posthog-node, AWS SDK, etc.) that the platform `index.ts` re-exports.
//
// Settings stays the caller's job — modal builds from UI toggles, chat
// passes undefined (server backfills defaults), API accepts as a body field.
export const buildPostContentFromWorkspace = (
  workspace: WorkspaceLike,
  platform: PublishablePlatformType,
  flags: WorkspacePostFlags = {},
): BuildPostContentResult => {
  const uploadUrls = workspace.media
    .filter((m) => m.file.url)
    .map((m) => ({ url: m.file.url, file: { type: fileTypeFromMediaType(m.type) } }))

  switch (platform) {
    case 'linkedin': {
      const result = linkedinPostContent(
        uploadUrls,
        workspace.captions.linkedin ?? '',
        workspace.title,
      )
      return result.type === 'invalid'
        ? { ok: false, reason: result.reason }
        : { ok: true, content: { platform: 'linkedin', postContent: result } }
    }
    case 'instagram': {
      const result = instagramPostContent(
        uploadUrls,
        workspace.captions.instagram ?? '',
        flags.isStory ?? false,
      )
      return result.type === 'invalid'
        ? { ok: false, reason: result.reason }
        : { ok: true, content: { platform: 'instagram', postContent: result } }
    }
    case 'tiktok': {
      const result = tiktokPostContent(uploadUrls, workspace.captions.tiktok ?? '')
      return result.type === 'invalid'
        ? { ok: false, reason: result.reason }
        : { ok: true, content: { platform: 'tiktok', postContent: result } }
    }
    case 'youtube': {
      // YouTube's caption-equivalent is the long-form description; the title
      // is a separate workspace field (falls back to the workspace title).
      const description = workspace.captions.youtube ?? ''
      const title = workspace.titles.youtube ?? workspace.title
      const result = youtubePostContent(uploadUrls, description, title)
      return result.type === 'invalid'
        ? { ok: false, reason: result.reason }
        : { ok: true, content: { platform: 'youtube', postContent: result } }
    }
    case 'facebook': {
      const result = facebookPostContent(
        uploadUrls,
        workspace.captions.facebook ?? '',
        flags.isStory ?? false,
        flags.isReel ?? false,
      )
      return result.type === 'invalid'
        ? { ok: false, reason: result.reason }
        : { ok: true, content: { platform: 'facebook', postContent: result } }
    }
  }
}
